// P0 (Batch 1.5) — real reachability for React Query's onlineManager.
//
// By default onlineManager keys purely off navigator.onLine, which is `true`
// on captive / connected-but-no-internet school wifi — so React Query treats
// the network as up and lets queries fire into a black hole. This wires
// onlineManager to a real reachability check: a `no-cors` ping (opaque
// response — resolves iff the server is reachable, rejects on a dead network,
// and sidesteps CORS entirely) with a short AbortController timeout. A failed
// browser-online event is verified before we report "online" so a captive
// portal can't unpause a flood of queries.

import { useSyncExternalStore } from "react";
import { onlineManager } from "@tanstack/react-query";
import { SUPABASE_URL } from "@/supabaseClient.js";

// MA-15: module-level registry for verified-online callbacks (e.g. outbox drain).
// Callbacks fire after the real reachability ping succeeds — NOT on every
// navigator.onLine=true (captive portals would trigger false fires).
// Fires on startup and on every offline→online transition; callers must be
// idempotent (the drain uses the circuit breaker + draining-flag guard).
const _onlineCallbacks = new Set<() => void>();

/** Register a callback that fires whenever the network is verified online.
 *  Returns an unregister function (call it in useEffect cleanup). */
export function registerOnlineCallback(fn: () => void): () => void {
  _onlineCallbacks.add(fn);
  return () => { _onlineCallbacks.delete(fn); };
}

async function reachable(timeoutMs = 4000): Promise<boolean> {
  if (typeof fetch === "undefined") return true; // SSR / old WebView — assume up
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    // no-cors: we don't read the body, only care that the network responded.
    // KNOWN LIMITATION: a no-cors fetch resolves on ANY opaque response, so a
    // captive portal that intercepts the request and serves its own 200 login
    // page will read as "reachable" here. The retry-cap + circuit breaker +
    // networkMode:'offlineFirst' are the real storm guards; this ping only
    // tightens the common "router up, internet down" case, not captive portals.
    await fetch(`${SUPABASE_URL}/auth/v1/health`, {
      method: "GET",
      mode: "no-cors",
      cache: "no-store",
      signal: controller.signal,
    });
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/** MA-347: the ONE reactive read of the verified reachability signal for
 *  components (e.g. OfflineBanner) — subscribes to the same `onlineManager`
 *  `initReachability()` wires above, so nothing builds a second probe.
 *  `() => true` server-snapshot keeps SSR-style renderers (renderToStaticMarkup
 *  in tests) from throwing when no browser globals exist. */
export function useIsOnline(): boolean {
  return useSyncExternalStore(
    (onStoreChange) => onlineManager.subscribe(onStoreChange),
    () => onlineManager.isOnline(),
    () => true,
  );
}

/** Install the reachability-backed online detector. Safe no-op in non-browser
 *  environments. Call once at startup (after the QueryClient exists). */
export function initReachability(): void {
  if (typeof window === "undefined") return;
  onlineManager.setEventListener((setOnline) => {
    const verify = async () => {
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        setOnline(false);
        return;
      }
      // Browser says online — confirm with a real ping (captive-portal guard).
      const up = await reachable();
      setOnline(up);
      // MA-15: fire outbox-drain and other online callbacks once verified up.
      if (up) _onlineCallbacks.forEach((fn) => fn());
    };
    const onOnline = () => void verify();
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    void verify();
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  });
}
